import { showNotice } from "../lib/confirmDialog";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PlayerFamilyProfileSwitcher from "../components/PlayerFamilyProfileSwitcher";
import { useAuth } from "../auth/AuthContext";
import type {
  FriendConnectionOut,
  FriendListResponse,
  FriendSearchResult,
  FriendlyMatchOut,
} from "../lib/api";
import { api } from "../lib/api";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import { scoreTextFromWinner } from "../lib/scoreFromWinner";

/** Amistoso: ¿ganó el jugador 1? null sin ganador. El marcador del amistoso
 * también se guarda desde el jugador 1 (Eduardo 2026-10-06). */
function friendlyWinnerIsPlayer1(match: FriendlyMatchOut): boolean | null {
  if (match.winner_profile_id == null) return null;
  if (match.winner_profile_id === match.player1.player_profile_id) return true;
  if (match.winner_profile_id === match.player2.player_profile_id) return false;
  return null;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function formatDateShort(dateStr: string | null): string {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y?.slice(2)}`;
}

function statusBadge(status: FriendSearchResult["friend_status"]): string {
  switch (status) {
    case "accepted": return "✅ Amigo";
    case "pending_sent": return "⏳ Enviada";
    case "pending_received": return "📩 Te envió solicitud";
    default: return "";
  }
}

function matchResultLabel(result: FriendlyMatchOut["result"]): string {
  switch (result) {
    case "win": return "🏆 Ganaste";
    case "loss": return "Perdiste";
    default: return "Pendiente";
  }
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "🎾";
}

function visiblePlayerId(playerId: string | null | undefined): string | null {
  if (!playerId) return null;
  return playerId.trim().toLowerCase().startsWith("legacy") ? null : playerId;
}

function friendMeta(friend: FriendConnectionOut): string[] {
  return [friend.friend.primary_category, friend.friend.city, friend.friend.club_name].filter(
    (value): value is string => Boolean(value),
  );
}

function ownProfileId(friend: FriendConnectionOut): number {
  return friend.friend.player_profile_id === friend.player_profile_id
    ? friend.friend_profile_id
    : friend.player_profile_id;
}

function isMatchVsProfile(match: FriendlyMatchOut, profileId: number): boolean {
  return match.player1.player_profile_id === profileId || match.player2.player_profile_id === profileId;
}

function matchesForFriend(matches: FriendlyMatchOut[], friend: FriendConnectionOut): FriendlyMatchOut[] {
  return matches.filter((match) => isMatchVsProfile(match, friend.friend.player_profile_id));
}

function gameStatsForFriend(matches: FriendlyMatchOut[], friend: FriendConnectionOut): { won: number; lost: number } {
  const me = ownProfileId(friend);
  const opponent = friend.friend.player_profile_id;
  return matchesForFriend(matches, friend).reduce(
    (totals, match) => {
      if (!match.score_text) return totals;
      const player1IsMe = match.player1.player_profile_id === me;
      const player2IsMe = match.player2.player_profile_id === me;
      if (!player1IsMe && !player2IsMe) return totals;
      if (!isMatchVsProfile(match, opponent)) return totals;
      const pairs = [...match.score_text.matchAll(/(\d+)\s*[-/]\s*(\d+)/g)];
      pairs.forEach((pair) => {
        const p1 = Number(pair[1]);
        const p2 = Number(pair[2]);
        if (!Number.isFinite(p1) || !Number.isFinite(p2)) return;
        totals.won += player1IsMe ? p1 : p2;
        totals.lost += player1IsMe ? p2 : p1;
      });
      return totals;
    },
    { won: 0, lost: 0 },
  );
}

const FRIENDLY_SCORE_COLUMNS = ["Set 1", "Set 2", "Set 3", "Set 4", "Set 5", "TB"] as const;
type FriendlyScoreColumn = (typeof FRIENDLY_SCORE_COLUMNS)[number];
type FriendlyScoreDraft = Record<"p1" | "p2", Record<FriendlyScoreColumn, string>>;

function emptyScoreDraft(): FriendlyScoreDraft {
  return {
    p1: { "Set 1": "", "Set 2": "", "Set 3": "", "Set 4": "", "Set 5": "", TB: "" },
    p2: { "Set 1": "", "Set 2": "", "Set 3": "", "Set 4": "", "Set 5": "", TB: "" },
  };
}

function friendlyScoreTextFromDraft(draft: FriendlyScoreDraft): string {
  return FRIENDLY_SCORE_COLUMNS.flatMap((column) => {
    const p1 = draft.p1[column].trim();
    const p2 = draft.p2[column].trim();
    if (!p1 && !p2) return [];
    const score = `${p1 || "0"}-${p2 || "0"}`;
    return column === "TB" ? [`TB ${score}`] : [score];
  }).join(", ");
}

function friendlySetsFromDraft(draft: FriendlyScoreDraft): { p1: number; p2: number } {
  return FRIENDLY_SCORE_COLUMNS.reduce(
    (totals, column) => {
      const p1 = Number(draft.p1[column]);
      const p2 = Number(draft.p2[column]);
      if (!Number.isFinite(p1) || !Number.isFinite(p2) || (p1 === 0 && p2 === 0)) return totals;
      if (p1 > p2) totals.p1 += 1;
      if (p2 > p1) totals.p2 += 1;
      return totals;
    },
    { p1: 0, p2: 0 },
  );
}

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function PlayerFriendsPage() {
  const { token } = useAuth();

  const [friends, setFriends] = useState<FriendListResponse | null>(null);
  // Perfil activo de la cuenta familiar (una sola fuente, Eduardo 2026-09-29).
  const scope = usePlayerProfileScope();
  const familyProfiles = scope.profiles;
  const activeProfileId = scope.profileId;
  const { askConfirm } = useAbortableConfirm([activeProfileId]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<FriendSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [fmList, setFmList] = useState<FriendlyMatchOut[]>([]);
  const [fmLoading, setFmLoading] = useState(false);
  const [createMatchFriend, setCreateMatchFriend] = useState<FriendConnectionOut | null>(null);
  const [matchDate, setMatchDate] = useState("");
  const [matchNotes, setMatchNotes] = useState("");
  const [matchSubmitting, setMatchSubmitting] = useState(false);

  const [reportMatch, setReportMatch] = useState<FriendlyMatchOut | null>(null);
  const [reportWinner, setReportWinner] = useState<number | null>(null);
  const [reportScoreDraft, setReportScoreDraft] = useState<FriendlyScoreDraft>(() => emptyScoreDraft());
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const [activeTab, setActiveTab] = useState<"friends" | "requests" | "search" | "matches">("friends");

  // Cuenta familiar: al cambiar de hijo (aquí o en otra pestaña) no queda en
  // pantalla nada del anterior — ni amigos, ni amistosos, ni búsqueda, ni
  // ventanas abiertas — y una respuesta tardía del anterior no se pinta.
  const friendsLoad = useMemo(() => makeLatestOnly(), []);
  const matchesLoad = useMemo(() => makeLatestOnly(), []);
  const searchLoad = useMemo(() => makeLatestOnly(), []);
  const [shownProfileId, setShownProfileId] = useState(activeProfileId);
  if (shownProfileId !== activeProfileId) {
    setShownProfileId(activeProfileId);
    setFriends(null);
    setFmList([]);
    setSearchResults([]);
    setSearchQuery("");
    setSearchLoading(false);
    setCreateMatchFriend(null);
    setReportMatch(null);
  }
  // Perfil vigente para descartar respuestas de otro hijo (también las de
  // una acción que empezó antes del cambio y recarga al terminar).
  const currentProfileRef = useRef(activeProfileId);
  useEffect(() => {
    currentProfileRef.current = activeProfileId;
    searchLoad.invalidate();
  }, [activeProfileId, searchLoad]);
  const isShownProfile = useCallback((profileId: number | null) => currentProfileRef.current === profileId, []);

  /* ── Load data ─────────────────────────────── */
  const loadFriends = useCallback(async () => {
    // Una recarga pedida por una acción del hijo anterior ya no aplica.
    if (!token || !scope.ready || !isShownProfile(activeProfileId)) return;
    const attempt = friendsLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const resp = await api.player.friends.list(token, activeProfileId);
      if (!friendsLoad.isCurrent(attempt) || !isShownProfile(activeProfileId)) return;
      if (resp.ok) setFriends(resp.data);
    } catch {
      if (!friendsLoad.isCurrent(attempt) || !isShownProfile(activeProfileId)) return;
      setError("No se pudo cargar la lista de amigos");
    } finally {
      if (friendsLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [activeProfileId, friendsLoad, isShownProfile, scope.ready, token]);

  const loadFriendlyMatches = useCallback(async () => {
    if (!token || !scope.ready || !isShownProfile(activeProfileId)) return;
    const attempt = matchesLoad.begin();
    setFmLoading(true);
    try {
      const resp = await api.player.friendlyMatches.list(token, activeProfileId);
      if (!matchesLoad.isCurrent(attempt) || !isShownProfile(activeProfileId)) return;
      if (resp.ok) setFmList(resp.data);
    } finally {
      if (matchesLoad.isCurrent(attempt)) setFmLoading(false);
    }
  }, [activeProfileId, isShownProfile, matchesLoad, scope.ready, token]);

  useEffect(() => {
    loadFriends();
    loadFriendlyMatches();
  }, [loadFriends, loadFriendlyMatches]);

  const selectedProfileId = activeProfileId ?? familyProfiles[0]?.id ?? null;
  const selectedProfileName = familyProfiles.find((profile) => profile.id === selectedProfileId)?.display_name;
  const handleProfileChange = (profileId: number) => {
    scope.setProfileId(profileId);
  };

  /* ── Search ────────────────────────────────── */
  const doSearch = useCallback(async () => {
    if (!token || searchQuery.trim().length < 2 || !isShownProfile(activeProfileId)) return;
    const attempt = searchLoad.begin();
    setSearchLoading(true);
    try {
      const resp = await api.player.friends.search(token, searchQuery.trim(), activeProfileId);
      if (!searchLoad.isCurrent(attempt) || !isShownProfile(activeProfileId)) return;
      if (resp.ok) setSearchResults(resp.data);
    } finally {
      if (searchLoad.isCurrent(attempt)) setSearchLoading(false);
    }
  }, [activeProfileId, isShownProfile, searchLoad, token, searchQuery]);

  const sendRequest = useCallback(
    async (profileId: number) => {
      if (!token) return;
      try {
        await api.player.friends.sendRequest(token, profileId, activeProfileId);
        doSearch();
      } catch (e: unknown) {
        void showNotice(e instanceof Error ? e.message : "Error");
      }
    },
    [activeProfileId, token, doSearch],
  );

  const respondRequest = useCallback(
    async (friendId: number, action: "accept" | "reject") => {
      if (!token) return;
      try {
        await api.player.friends.respond(token, friendId, action, activeProfileId);
        loadFriends();
      } catch (e: unknown) {
        void showNotice(e instanceof Error ? e.message : "Error");
      }
    },
    [activeProfileId, token, loadFriends],
  );

  const removeFriend = useCallback(
    async (profileId: number) => {
      if (!token) return;
      if (!(await askConfirm("¿Eliminar a este amigo?", { acceptLabel: "Sí, eliminar amigo", tone: "danger" }))) return;
      try {
        await api.player.friends.remove(token, profileId, activeProfileId);
        loadFriends();
        loadFriendlyMatches();
      } catch (e: unknown) {
        void showNotice(e instanceof Error ? e.message : "Error");
      }
    },
    [activeProfileId, token, loadFriends, loadFriendlyMatches],
  );

  const createFriendlyMatch = useCallback(async () => {
    if (!token || !createMatchFriend) return;
    setMatchSubmitting(true);
    try {
      await api.player.friendlyMatches.create(token, {
        friend_profile_id: createMatchFriend.friend.player_profile_id,
        played_at: matchDate || undefined,
        notes: matchNotes || undefined,
      }, activeProfileId);
      setCreateMatchFriend(null);
      setMatchDate("");
      setMatchNotes("");
      await loadFriendlyMatches();
      await loadFriends();
      setActiveTab("matches");
    } catch (e: unknown) {
      void showNotice(e instanceof Error ? e.message : "Error");
    } finally {
      setMatchSubmitting(false);
    }
  }, [activeProfileId, token, createMatchFriend, matchDate, matchNotes, loadFriendlyMatches, loadFriends]);

  const openReportMatch = useCallback((match: FriendlyMatchOut) => {
    setReportMatch(match);
    setReportWinner(match.winner_profile_id);
    setReportScoreDraft(emptyScoreDraft());
  }, []);

  const updateReportScore = useCallback(
    (player: "p1" | "p2", column: FriendlyScoreColumn, value: string) => {
      setReportScoreDraft((current) => ({
        ...current,
        [player]: {
          ...current[player],
          [column]: value,
        },
      }));
    },
    [],
  );

  const reportFriendlyResult = useCallback(async () => {
    if (!token || !reportMatch || reportWinner === null) return;
    const sets = friendlySetsFromDraft(reportScoreDraft);
    const scoreText = friendlyScoreTextFromDraft(reportScoreDraft);
    setReportSubmitting(true);
    try {
      await api.player.friendlyMatches.reportResult(token, reportMatch.id, {
        winner_profile_id: reportWinner,
        score_p1_sets: sets.p1 || undefined,
        score_p2_sets: sets.p2 || undefined,
        score_text: scoreText || undefined,
      }, activeProfileId);
      setReportMatch(null);
      setReportWinner(null);
      setReportScoreDraft(emptyScoreDraft());
      await loadFriendlyMatches();
      await loadFriends();
    } catch (e: unknown) {
      void showNotice(e instanceof Error ? e.message : "Error");
    } finally {
      setReportSubmitting(false);
    }
  }, [activeProfileId, token, reportMatch, reportWinner, reportScoreDraft, loadFriendlyMatches, loadFriends]);

  const pendingCount = (friends?.pending_received?.length ?? 0) + (friends?.pending_sent?.length ?? 0);

  return (
    <section className="player-friends-page" aria-label="Amigos">
      <header className="player-friends-page__header player-friends-page__header--pro">
        <div>
          <span className="player-friends-page__eyebrow">Red de jugadores</span>
          <h1>👥 Amigos</h1>
          <p>Conecta, reta y lleva un historial serio de tus partidos amistosos{selectedProfileName ? ` de ${selectedProfileName}` : ""}.</p>
        </div>
        <PlayerFamilyProfileSwitcher
          familyProfiles={familyProfiles}
          activeProfileId={selectedProfileId}
          onChange={handleProfileChange}
        />
      </header>

      {/* ── Tabs ────────────────────────────────── */}
      <nav className="player-friends-page__tabs">
        <button
          type="button"
          className={activeTab === "friends" ? "is-active" : ""}
          onClick={() => setActiveTab("friends")}
        >
          Amigos {friends ? `(${friends.friends.length})` : ""}
        </button>
        <button
          type="button"
          className={activeTab === "requests" ? "is-active" : ""}
          onClick={() => setActiveTab("requests")}
        >
          Solicitudes {pendingCount > 0 ? `(${pendingCount})` : ""}
        </button>
        <button
          type="button"
          className={activeTab === "matches" ? "is-active" : ""}
          onClick={() => setActiveTab("matches")}
        >
          Partidos {fmList.length > 0 ? `(${fmList.length})` : ""}
        </button>
        <button
          type="button"
          className={activeTab === "search" ? "is-active" : ""}
          onClick={() => setActiveTab("search")}
        >
          🔍 Buscar
        </button>
      </nav>

      {/* ── Friends tab ─────────────────────────── */}
      {activeTab === "friends" && (
        <div className="player-friends-page__tab-body">
          {loading ? (
            <p className="player-friends-page__empty">Cargando amigos...</p>
          ) : error ? (
            <p className="player-friends-page__error">{error}</p>
          ) : friends?.friends.length === 0 ? (
            <p className="player-friends-page__empty">
              Aún no tienes amigos. Ve a la pestaña "Buscar" para encontrar jugadores.
            </p>
          ) : (
            <div className="player-friends-page__list player-friends-page__list--pro">
              {friends?.friends.map((f) => {
                const meta = friendMeta(f);
                const totalMatches = f.h2h_summary.wins + f.h2h_summary.losses + f.h2h_summary.draws;
                const friendlyMatches = matchesForFriend(fmList, f);
                const pendingMatch = friendlyMatches.find((match) => match.result === "unknown");
                const gameStats = gameStatsForFriend(fmList, f);
                return (
                  <div key={f.id} className="player-friends-page__card player-friends-page__friend-card-pro">
                    <div className="player-friends-page__avatar" aria-hidden="true">
                      {initials(f.friend.display_name)}
                    </div>
                    <div className="player-friends-page__friend-main">
                      <div className="player-friends-page__friend-title-row">
                        <div>
                          <strong>{f.friend.display_name}</strong>
                          {visiblePlayerId(f.friend.unique_player_id) && (
                            <span className="player-friends-page__card-id">#{visiblePlayerId(f.friend.unique_player_id)}</span>
                          )}
                        </div>
                        <span className="player-friends-page__record-pill">
                          Record {f.h2h_summary.wins}-{f.h2h_summary.losses}
                        </span>
                      </div>
                      {meta.length > 0 && (
                        <div className="player-friends-page__friend-meta">
                          {meta.map((item) => <span key={item}>{item}</span>)}
                        </div>
                      )}
                      <div className="player-friends-page__h2h-grid" aria-label={`Historial contra ${f.friend.display_name}`}>
                        <div className="player-friends-page__h2h-stat player-friends-page__h2h-stat--record">
                          <span>Record</span>
                          <strong>{f.h2h_summary.wins}-{f.h2h_summary.losses}</strong>
                          <small>{totalMatches} partido{totalMatches !== 1 ? "s" : ""}</small>
                        </div>
                        <div className="player-friends-page__h2h-stat">
                          <span>Partidos ganados</span>
                          <strong>{f.h2h_summary.wins}</strong>
                        </div>
                        <div className="player-friends-page__h2h-stat">
                          <span>Partidos perdidos</span>
                          <strong>{f.h2h_summary.losses}</strong>
                        </div>
                        <div className="player-friends-page__h2h-stat">
                          <span>GG - GP</span>
                          <strong>{gameStats.won}-{gameStats.lost}</strong>
                        </div>
                      </div>
                      <div className="player-friends-page__friend-footnote">
                        {f.h2h_summary.last_score ? (
                          <span>Último score: {f.h2h_summary.last_score}{f.h2h_summary.last_date ? ` · ${formatDateShort(f.h2h_summary.last_date)}` : ""}</span>
                        ) : (
                          <span>Sin resultado registrado todavía.</span>
                        )}
                        {pendingMatch && <span className="player-friends-page__pending-chip">Pendiente de reportar</span>}
                      </div>
                    </div>
                    <div className="player-friends-page__card-actions player-friends-page__friend-actions-pro">
                      <button
                        type="button"
                        className="player-friends-page__btn"
                        onClick={() => setActiveTab("matches")}
                      >
                        🎾 Ver partidos
                      </button>
                      <button
                        type="button"
                        className="player-friends-page__btn player-friends-page__btn--report"
                        onClick={() => pendingMatch && openReportMatch(pendingMatch)}
                        disabled={!pendingMatch}
                        title={pendingMatch ? "Reportar resultado pendiente" : "No hay partido pendiente contra este amigo"}
                      >
                        ✏️ Reportar
                      </button>
                      <button
                        type="button"
                        className="player-friends-page__btn player-friends-page__btn--challenge"
                        onClick={() => setCreateMatchFriend(f)}
                      >
                        🎾 Crear amistoso
                      </button>
                      <button
                        type="button"
                        className="player-friends-page__btn player-friends-page__btn--danger"
                        onClick={() => removeFriend(f.friend.player_profile_id)}
                        aria-label={`Eliminar a ${f.friend.display_name}`}
                        title="Eliminar amigo"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Requests tab ────────────────────────── */}
      {activeTab === "requests" && (
        <div className="player-friends-page__tab-body">
          {friends?.pending_received.length === 0 && friends?.pending_sent.length === 0 ? (
            <p className="player-friends-page__empty">No hay solicitudes pendientes.</p>
          ) : (
            <>
              {friends && friends.pending_received.length > 0 && (
                <div className="player-friends-page__section">
                  <h3>📩 Recibidas</h3>
                  {friends.pending_received.map((req) => (
                    <div key={req.id} className="player-friends-page__card">
                      <div className="player-friends-page__card-info">
                        <strong>{req.friend.display_name}</strong>
                      </div>
                      <div className="player-friends-page__card-actions">
                        <button
                          type="button"
                          className="player-friends-page__btn player-friends-page__btn--accept"
                          onClick={() => respondRequest(req.id, "accept")}
                        >
                          ✅ Aceptar
                        </button>
                        <button
                          type="button"
                          className="player-friends-page__btn player-friends-page__btn--danger"
                          onClick={() => respondRequest(req.id, "reject")}
                        >
                          ✕ Rechazar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {friends && friends.pending_sent.length > 0 && (
                <div className="player-friends-page__section">
                  <h3>⏳ Enviadas</h3>
                  {friends.pending_sent.map((req) => (
                    <div key={req.id} className="player-friends-page__card player-friends-page__card--muted">
                      <div className="player-friends-page__card-info">
                        <strong>{req.friend.display_name}</strong>
                        <span>Esperando respuesta...</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Matches tab ─────────────────────────── */}
      {activeTab === "matches" && (
        <div className="player-friends-page__tab-body">
          {fmLoading ? (
            <p className="player-friends-page__empty">Cargando partidos...</p>
          ) : fmList.length === 0 ? (
            <p className="player-friends-page__empty">
              No hay partidos amistosos. Ve a la pestaña "Amigos" y crea uno.
            </p>
          ) : (
            <div className="player-friends-page__matches">
              {fmList.map((m) => (
                <div key={m.id} className="player-friends-page__match-card">
                  <div className="player-friends-page__match-header">
                    <span className={`player-friends-page__match-result player-friends-page__match-result--${m.result}`}>
                      {matchResultLabel(m.result)}
                    </span>
                    <span className="player-friends-page__match-date">
                      {m.played_at ? formatDateShort(m.played_at) : "Sin fecha"}
                    </span>
                  </div>
                  <div className="player-friends-page__match-players">
                    {/* Eduardo 2026-10-06: ganador en negritas y marcador desde quien ganó. */}
                    {friendlyWinnerIsPlayer1(m) === true ? <strong>{m.player1.display_name}</strong> : m.player1.display_name}
                    {" vs "}
                    {friendlyWinnerIsPlayer1(m) === false ? <strong>{m.player2.display_name}</strong> : m.player2.display_name}
                  </div>
                  {m.score_text && (
                    <div className="player-friends-page__match-score">
                      {scoreTextFromWinner(m.score_text, friendlyWinnerIsPlayer1(m))}
                    </div>
                  )}
                  {m.result === "unknown" && (
                    <button
                      type="button"
                      className="player-friends-page__btn player-friends-page__btn--report"
                      onClick={() => openReportMatch(m)}
                    >
                      ✏️ Reportar
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Search tab ──────────────────────────── */}
      {activeTab === "search" && (
        <div className="player-friends-page__tab-body">
          <div className="player-friends-page__search-row">
            <input
              type="text"
              className="player-friends-page__search-input"
              placeholder="Nombre o ID del jugador..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
            />
            <button
              type="button"
              className="player-friends-page__btn player-friends-page__btn--primary"
              onClick={doSearch}
              disabled={searchLoading || searchQuery.trim().length < 2}
            >
              {searchLoading ? "Buscando..." : "Buscar"}
            </button>
          </div>
          <div className="player-friends-page__list">
            {searchResults.map((p) => (
              <div key={p.player_profile_id} className="player-friends-page__card">
                <div className="player-friends-page__card-info">
                  <strong>{p.display_name}</strong>
                  {visiblePlayerId(p.unique_player_id) && (
                    <span className="player-friends-page__card-id">#{visiblePlayerId(p.unique_player_id)}</span>
                  )}
                </div>
                <div className="player-friends-page__card-actions">
                  {p.friend_status ? (
                    <span className="player-friends-page__status">{statusBadge(p.friend_status)}</span>
                  ) : (
                    <button
                      type="button"
                      className="player-friends-page__btn player-friends-page__btn--accept"
                      onClick={() => sendRequest(p.player_profile_id)}
                    >
                      ➕ Agregar
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!searchLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
              <p className="player-friends-page__empty">No se encontraron jugadores.</p>
            )}
          </div>
        </div>
      )}

      {createMatchFriend && (
        <div className="amigos-modal__overlay" onClick={() => setCreateMatchFriend(null)}>
          <div className="amigos-modal" onClick={(event) => event.stopPropagation()}>
            <div className="amigos-modal__header">
              <h3>🎾 Partido amistoso vs {createMatchFriend.friend.display_name}</h3>
              <button type="button" className="amigos-modal__close" onClick={() => setCreateMatchFriend(null)}>
                ✕
              </button>
            </div>
            <div className="amigos-modal__body">
              <p className="amigos-modal__subtitle">
                Captura fecha y notas. Después podrás registrar el score desde Partidos.
              </p>
              <label className="amigos-modal__label">Fecha del partido</label>
              <input
                type="date"
                className="amigos-modal__input"
                value={matchDate}
                onChange={(event) => setMatchDate(event.target.value)}
              />
              <label className="amigos-modal__label">Notas (opcional)</label>
              <textarea
                className="amigos-modal__input amigos-modal__textarea"
                placeholder="¿Dónde jugaron? ¿Algún detalle?"
                value={matchNotes}
                onChange={(event) => setMatchNotes(event.target.value)}
                rows={2}
              />
              <div className="amigos-modal__actions">
                <button
                  type="button"
                  className="player-friends-page__btn"
                  onClick={() => setCreateMatchFriend(null)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="player-friends-page__btn player-friends-page__btn--challenge"
                  onClick={createFriendlyMatch}
                  disabled={matchSubmitting}
                >
                  {matchSubmitting ? "Creando..." : "Crear partido amistoso"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {reportMatch && (
        <div className="amigos-modal__overlay" onClick={() => setReportMatch(null)}>
          <div className="amigos-modal amigos-modal--score" onClick={(event) => event.stopPropagation()}>
            <div className="amigos-modal__header amigos-modal__header--score">
              <div>
                <span className="amigos-modal__eyebrow">Partido amistoso</span>
                <h3>✏️ Reportar resultado</h3>
              </div>
              <button type="button" className="amigos-modal__close" onClick={() => setReportMatch(null)}>
                ✕
              </button>
            </div>
            <div className="amigos-modal__body amigos-result-report">
              <div className="amigos-result-report__matchup" aria-label="Jugadores del amistoso">
                <span>{reportMatch.player1.display_name}</span>
                <strong>vs</strong>
                <span>{reportMatch.player2.display_name}</span>
              </div>

              <div className="amigos-result-scoreboard" aria-label="Captura de score amistoso por set">
                <div className="amigos-result-scoreboard__grid">
                  <div className="amigos-result-scoreboard__corner">Jugador</div>
                  <div className="amigos-result-scoreboard__winner-label">Ganador</div>
                  {FRIENDLY_SCORE_COLUMNS.map((column) => (
                    <div key={column} className="amigos-result-scoreboard__set-label">
                      {column}
                    </div>
                  ))}

                  {[reportMatch.player1, reportMatch.player2].map((player, index) => {
                    const rowKey = index === 0 ? "p1" : "p2";
                    const displayName = player.display_name ?? `Jugador #${player.player_profile_id}`;
                    return (
                      <div className="amigos-result-scoreboard__row" key={player.player_profile_id}>
                        <div className="amigos-result-scoreboard__player">
                          <span className="amigos-result-scoreboard__name">{displayName}</span>
                        </div>
                        <label className="amigos-result-scoreboard__winner-cell" aria-label={`Ganador ${displayName}`}>
                          <input
                            type="radio"
                            name={`friendly-winner-${reportMatch.id}`}
                            checked={reportWinner === player.player_profile_id}
                            onChange={() => setReportWinner(player.player_profile_id)}
                          />
                          <span className="amigos-result-scoreboard__winner-indicator">✓</span>
                        </label>
                        {FRIENDLY_SCORE_COLUMNS.map((column) => (
                          <input
                            key={column}
                            className="amigos-result-scoreboard__score-input"
                            aria-label={`${column} ${displayName}`}
                            type="number"
                            min={0}
                            max={99}
                            inputMode="numeric"
                            value={reportScoreDraft[rowKey][column]}
                            onChange={(event) => updateReportScore(rowKey, column, event.target.value)}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              <p className="amigos-result-report__hint">
                Captura los juegos por jugador. Hay espacio para hasta 5 sets y un TB; el sistema arma el score y los sets ganados automáticamente.
              </p>

              <div className="amigos-result-report__preview">
                <span>Score</span>
                <strong>{friendlyScoreTextFromDraft(reportScoreDraft) || "—"}</strong>
              </div>

              <div className="amigos-modal__actions amigos-modal__actions--sticky">
                <button
                  type="button"
                  className="player-friends-page__btn"
                  onClick={() => setReportMatch(null)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="player-friends-page__btn player-friends-page__btn--challenge"
                  onClick={reportFriendlyResult}
                  disabled={reportSubmitting || reportWinner === null}
                >
                  {reportSubmitting ? "Guardando..." : "Guardar resultado"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

